import { useQuery } from "@apollo/client/react";
import { useCallback, useEffect, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";

import ReadMoreDocsLink from "components/ReadMoreDocsLink";
import ComboBox from "ds/components/ComboBox";
import ComboBoxDefaultItem from "ds/components/ComboBox/DefaultItem";
import useComboBoxInputValueItem from "ds/components/ComboBox/useInputValueItem";
import { BaseSelectItem } from "ds/components/Select/types";
import TooltipModalBody from "ds/components/TooltipModal/Body";
import { TooltipModalTitle } from "ds/components/TooltipModal/Title";
import { getDocsUrl } from "utils/getDocsUrl";
import { checkWithMultipleVCSIntegrations } from "utils/vcs";

import { StackVcsFormFields } from "../../types";
import useStackCreationAnalyticsVersion from "../../useStackCreationAnalyticsVersion";
import { getTooltipAnalyticsProps } from "../../utils";
import { GET_BRANCHES, GetBranchesGql } from "./gql";

const INPUT_VALUE_ITEM_PROPS = {
  label: "Can't find your branch? Select this option to add it anyway.",
};

const VcsBranchesField = () => {
  const { control, setValue, watch, setError, clearErrors } = useFormContext<StackVcsFormFields>();
  const analyticsVersion = useStackCreationAnalyticsVersion();

  const formValues = watch();

  const withMultipleVCSIntegrations = checkWithMultipleVCSIntegrations(formValues.provider);

  const { loading, data, error } = useQuery<GetBranchesGql>(GET_BRANCHES, {
    variables: {
      provider: formValues.provider,
      namespace: formValues.namespace,
      repository: formValues.repository,
      repositoryURL: formValues.repositoryURL,
      vcsIntegrationId: withMultipleVCSIntegrations ? formValues.vcsIntegrationId : null,
    },
  });

  const notExistingBranchOption = useMemo<BaseSelectItem | undefined>(() => {
    if (!data?.branches) return undefined;

    const currentValue = formValues.branch;
    const hasCurrentValueInList = data.branches.includes(currentValue);

    if (!hasCurrentValueInList && currentValue) {
      return {
        value: currentValue,
        label: currentValue,
      };
    }

    return undefined;
  }, [data?.branches, formValues.branch]);

  useEffect(() => {
    if (data?.branches) {
      const currentValue = watch("branch");
      const selectedBranch = currentValue || data.branches[0];

      setValue("branch", selectedBranch, { shouldValidate: true, shouldDirty: true });
      clearErrors("branch");
    }
  }, [data?.branches, clearErrors, setValue, watch]);

  useEffect(() => {
    if (error) {
      setError("branch", {
        type: "custom",
        message: error.message,
      });
    }
  }, [error, setError]);

  const branchesOptions: BaseSelectItem[] = useMemo(
    () => data?.branches.map((value) => ({ label: value, value })) ?? [],
    [data?.branches]
  );

  const options = useMemo(
    () => [...(notExistingBranchOption ? [notExistingBranchOption] : []), ...branchesOptions],
    [branchesOptions, notExistingBranchOption]
  );

  const onInputValueSelected = useCallback(
    (value: string) => {
      setValue("branch", value, { shouldValidate: true, shouldDirty: true });
    },
    [setValue]
  );

  const { comboBoxProps } = useComboBoxInputValueItem({
    onInputValueSelected,
    options,
    inputValueItemProps: INPUT_VALUE_ITEM_PROPS,
  });

  return (
    <Controller
      name="branch"
      control={control}
      rules={{ required: "Branch is required" }}
      render={({ field, fieldState }) => (
        <ComboBox
          label="Branch"
          {...getTooltipAnalyticsProps("Source Code", "Branch", {
            provider: formValues.provider,
            version: analyticsVersion,
          })}
          tooltipInfo={
            <>
              <TooltipModalTitle>Branch</TooltipModalTitle>
              <TooltipModalBody align="start">
                Git branch from which this stack is built. Note that the list in the dropdown is not
                exhaustive because for performance reasons we don’t do pagination. If your desired
                branch is not on the list, you can still enter it manually.
                <ReadMoreDocsLink
                  docsUrl={getDocsUrl(
                    "/concepts/stack/stack-settings#vcs-integration-and-repository"
                  )}
                />
              </TooltipModalBody>
            </>
          }
          error={(!loading && fieldState.error?.message) || undefined}
          value={field.value}
          {...comboBoxProps}
          onChange={comboBoxProps.onChange(field.onChange)}
          isLoading={loading}
        >
          {(item) => <ComboBoxDefaultItem id={item.value} {...item} />}
        </ComboBox>
      )}
    />
  );
};

export default VcsBranchesField;
